import type { Specimen } from '@numistic/contracts'
import {
  DataError,
  useCatalogItem,
  useCopyCatalogImages,
  useMeta,
  useSpecimen,
  useUpdateSpecimen,
} from '@numistic/data'
import { fieldsForType } from '@numistic/domain'
import { useTranslation } from '@numistic/i18n'
import { Alert, Button, Card, CardContent } from '@numistic/ui'
import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'

import { ImagesManager } from '../../catalog/images-manager.js'
import { MutationErrors } from '../../components/mutation-errors.js'
import { PageLoading } from '../../components/page-loading.js'
import { useErrorText } from '../../lib/errors.js'
import { requireSession } from '../../lib/guards.js'
import { MetaForm } from '../../meta/meta-form.js'
import { ExtraRefOptions } from '../../meta/widgets/ref-options.js'
import { rootRoute } from '../root.js'

const Tab = z.enum(['data', 'images'])
/** В форме экземпляра сначала его собственные поля, потом — данные каталога. */
const SECTION_ORDER = ['specimen', 'main', 'physical', 'paper', 'design', 'notes'] as const

export const specimenEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/s/$id/edit',
  validateSearch: z.object({ tab: Tab.optional().catch(undefined) }),
  beforeLoad: ({ context, location }) => requireSession(context, location),
  component: EditSpecimenPage,
})

function EditSpecimenPage() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const { id } = specimenEditRoute.useParams()
  const { tab = 'data' } = specimenEditRoute.useSearch()
  const specimen = useSpecimen(id)
  const meta = useMeta().data
  const update = useUpdateSpecimen()
  const navigate = useNavigate()
  const item = useCatalogItem(specimen.data?.catalogItem.id ?? '')
  // Версия, с которой начата правка: фоновое обновление не пересоздаёт форму и не теряет ввод.
  const [baseVersion, setBaseVersion] = useState<number | null>(null)
  if (specimen.data && baseVersion === null) setBaseVersion(specimen.data.version)
  // Поля каталога для переопределения: свёрнуты, если своих значений в них нет.
  const [overrides, setOverrides] = useState<boolean | null>(null)
  if (specimen.data && meta && overrides === null)
    setOverrides(
      fieldsForType(meta, specimen.data.catalogItem.typeCode, 'specimen').some(
        (f) => f.override && specimen.data!.values[f.def.code] !== undefined,
      ),
    )
  if (specimen.error)
    return <Alert variant="destructive">{errorText.request(specimen.error)}</Alert>
  const data = specimen.data
  if (!data || !meta) return <PageLoading />
  // Что стоит в каталоге (с учётом разновидности) — подсказка под каждым полем каталога.
  const variant = item.data?.variants.find((v) => v.id === data.variantId)
  const inherited = { ...item.data?.values, ...variant?.values }

  return (
    <div className="grid max-w-4xl gap-6">
      <div className="grid gap-1">
        <Link
          to="/s/$id"
          params={{ id }}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← {String(data.resolved.values.title ?? '')}
        </Link>
        <h1 className="text-2xl font-semibold">{t('catalog.edit')}</h1>
      </div>
      <nav className="flex gap-6 border-b">
        {Tab.options.map((x) => (
          <Link
            key={x}
            to="/s/$id/edit"
            params={{ id }}
            search={{ tab: x }}
            aria-current={tab === x ? 'page' : undefined}
            className="border-b-2 border-transparent px-1 pb-2 text-sm text-muted-foreground hover:text-foreground aria-[current=page]:border-primary aria-[current=page]:text-foreground"
          >
            {x === 'data'
              ? t('catalog.tabs.data')
              : `${t('specimen.photos')}${data.images.length ? ` (${data.images.length})` : ''}`}
          </Link>
        ))}
      </nav>
      <Card>
        <CardContent className="pt-6">
          {tab === 'data' ? (
            <ExtraRefOptions.Provider
              value={{
                variants: (item.data?.variants ?? []).map((v) => ({ id: v.id, label: v.label })),
              }}
            >
              <MetaForm
                key={data.id}
                typeCode={data.catalogItem.typeCode}
                level="specimen"
                defaultValues={data.values}
                includeOverrides={overrides ?? false}
                inherited={inherited}
                sectionOrder={[...SECTION_ORDER]}
                header={
                  <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 p-3">
                    <p className="mr-auto text-sm text-muted-foreground">
                      {overrides ? t('specimen.overridesOn') : t('specimen.overridesOff')}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-expanded={overrides ?? false}
                      onClick={() => setOverrides((v) => !v)}
                    >
                      {overrides ? t('specimen.hideOverrides') : t('specimen.showOverrides')}
                    </Button>
                  </div>
                }
                serverIssues={update.error instanceof DataError ? update.error.issues : undefined}
                footer={
                  update.error instanceof DataError && update.error.code === 'VERSION_CONFLICT' ? (
                    <Alert variant="destructive">{t('catalog.conflict')}</Alert>
                  ) : update.error ? (
                    <Alert variant="destructive">{errorText.request(update.error)}</Alert>
                  ) : null
                }
                pending={update.isPending}
                onSubmit={(values) =>
                  update.mutate(
                    { id, version: baseVersion ?? data.version, values },
                    { onSuccess: () => void navigate({ to: '/s/$id', params: { id } }) },
                  )
                }
              />
            </ExtraRefOptions.Provider>
          ) : (
            <SpecimenPhotos specimen={data} />
          )}
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Свои фото экземпляра. Пока их нет, на карточке показываются фото записи каталога — здесь они
 * видны, и одной кнопкой их можно сделать своими (дальше правятся отдельно от каталога).
 */
function SpecimenPhotos({ specimen }: { specimen: Specimen }) {
  const { t } = useTranslation()
  const copy = useCopyCatalogImages(specimen.id)
  const catalogOnly = !specimen.images.length && specimen.catalogImages.length > 0
  return (
    <div className="grid gap-5">
      {catalogOnly ? (
        <div className="grid gap-3 rounded-lg border bg-muted/30 p-4">
          <p className="text-sm">{t('specimen.catalogPhotosHint')}</p>
          <ul className="flex flex-wrap gap-2" aria-label={t('specimen.catalogPhotos')}>
            {specimen.catalogImages.map((img) => (
              <li key={img.id}>
                <img
                  src={img.urls.thumb}
                  alt={t(`catalog.images.roles.${img.role}`)}
                  className="size-20 rounded-md border bg-muted object-contain"
                />
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" disabled={copy.isPending} onClick={() => copy.mutate()}>
              {t('specimen.copyCatalogPhotos')}
            </Button>
            <span className="text-xs text-muted-foreground">{t('specimen.copyCatalogHint')}</span>
          </div>
          <MutationErrors errors={[copy.error]} />
        </div>
      ) : null}
      <ImagesManager subject={{ kind: 'specimen', id: specimen.id }} images={specimen.images} />
    </div>
  )
}
